import { useT } from "@/i18n";
import {
  HiOutlineChatAlt2,
  HiOutlineDocumentText,
  HiOutlineLightBulb,
  HiOutlineMenuAlt2,
  HiOutlineMicrophone,
} from "react-icons/hi";
import {
  EmptyState,
  FeedbackBox,
  Hero,
  PromptImage,
  ReadOnlyCard,
  Section,
  SpeakerButton,
} from "./ReadOnlyKit";
import { SPACING, rs } from "./readOnlyStyles";
import { getScoreColor } from "./scoring";
import type { ReadOnlyTemplateProps } from "./types";
import { useReviewAudio } from "./useReviewAudio";

/** Scored dimensions in display order; a dimension with no evidence comes back null and is skipped. */
const DIMENSIONS = [
  { key: "grammarScore", labelKey: "readonly.dimension.grammar", Icon: HiOutlineDocumentText },
  { key: "wordChoiceScore", labelKey: "readonly.dimension.wordChoice", Icon: HiOutlineMenuAlt2 },
  {
    key: "understandingScore",
    labelKey: "readonly.dimension.understanding",
    Icon: HiOutlineLightBulb,
  },
  {
    key: "communicationScore",
    labelKey: "readonly.dimension.communication",
    Icon: HiOutlineChatAlt2,
  },
  {
    key: "pronunciationScore",
    labelKey: "readonly.dimension.pronunciation",
    Icon: HiOutlineMicrophone,
  },
] as const;

const styles = {
  turn: {
    display: "flex",
    flexDirection: "column",
    gap: SPACING.xs,
    paddingBottom: SPACING.sm,
  },
  studentRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: SPACING.xs,
  },
  bubble: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: SPACING.sm,
    borderRadius: 16,
    padding: `${SPACING.sm}px ${SPACING.md}px`,
    maxWidth: "90%",
  },
  studentBubble: { backgroundColor: "#FFF7DD" },
  teacherBubble: { alignSelf: "flex-start", backgroundColor: "#F1F0FB" },
  bubbleText: {
    flexShrink: 1,
    minWidth: 0,
    overflowWrap: "anywhere",
    fontSize: 15,
    color: "#2C2B80",
  },
  turnScore: { fontSize: 13, fontWeight: 800, color: "#F4872A" },
  breakdownRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: SPACING.sm,
    backgroundColor: "#FAFAFC",
    borderRadius: 14,
    padding: `${SPACING.sm}px ${SPACING.md}px`,
  },
  breakdownIcon: {
    display: "flex",
    flexShrink: 0,
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
  },
  breakdownBody: { display: "flex", flexDirection: "column", flex: 1, gap: 6, minWidth: 0 },
  breakdownHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: SPACING.sm,
  },
  breakdownLabel: { fontSize: 14, fontWeight: 600, color: "#4B4B4B" },
  breakdownValue: { fontSize: 14, fontWeight: 800 },
  breakdownTrack: {
    height: 5,
    borderRadius: 3,
    backgroundColor: "#ECECF2",
    overflow: "hidden",
  },
  breakdownFill: { height: "100%", borderRadius: 3 },
  feedback: { marginTop: SPACING.sm },
} as const;

/**
 * A past conversation play, unfolded back into the exchange the student saw. Only the student's
 * side has audio — the teacher's was synthesized at play time and is not stored.
 */
export function ConversationReadOnly({
  snapshot,
  response,
  score,
  recordingUrl,
}: ReadOnlyTemplateProps<"conversation">) {
  const t = useT();
  const { text, correctAnswer, image } = snapshot.slideData;
  const { playingUrl, toggle } = useReviewAudio();
  const { breakdown } = response;

  return (
    <ReadOnlyCard title={snapshot.name} score={score}>
      {text || correctAnswer ? (
        <Hero>
          {text ? <p style={rs.heroSubtext}>{text}</p> : null}
          {correctAnswer ? <p style={rs.heroWord}>{correctAnswer}</p> : null}
          <PromptImage media={image} label={t["readonly.promptImage"]} />
        </Hero>
      ) : null}

      <Section label={t["readonly.conversation"]}>
        {response.turns.length === 0 ? (
          <EmptyState message={t["readonly.noTurns"]} />
        ) : (
          response.turns.map((turn, index) => {
            const url = recordingUrl(turn.recordingId);
            // The stored turn list is append-only and never reorders, so position is its identity.
            const turnKey = `turn-${index}`;

            return (
              <div key={turnKey} style={styles.turn}>
                {/* Speaker outside the bubble rather than in it: on the student's own yellow it
                    would have no chip to sit on. */}
                {turn.recognizedText || url ? (
                  <div style={styles.studentRow}>
                    <SpeakerButton
                      label={t["readonly.myTurn"]}
                      url={url}
                      isPlaying={playingUrl === url}
                      onPress={() => toggle(url)}
                      variant="student"
                    />
                    {turn.recognizedText ? (
                      <div style={{ ...styles.bubble, ...styles.studentBubble }}>
                        <span style={styles.bubbleText}>{turn.recognizedText}</span>
                        {turn.pronScore !== null && (
                          <span style={styles.turnScore}>{Math.round(turn.pronScore)}</span>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {/* The teacher's reply is stored as one string with "|||" marking the bubble
                    breaks it was spoken in, so play it back as the same run of bubbles. */}
                {turn.assistantText
                  .split("|||")
                  .map((part) => part.trim())
                  .filter(Boolean)
                  .map((part, partIndex) => {
                    const partKey = `${turnKey}-teacher-${partIndex}`;
                    return (
                      <div key={partKey} style={{ ...styles.bubble, ...styles.teacherBubble }}>
                        <span style={styles.bubbleText}>{part}</span>
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}
      </Section>

      {breakdown && (
        <Section label={t["readonly.assessment"]}>
          {DIMENSIONS.map(({ labelKey, Icon, key }) => {
            const value = breakdown[key];
            if (value === null) return null;
            const color = getScoreColor(value);

            return (
              <div key={labelKey} style={styles.breakdownRow}>
                <span style={{ ...styles.breakdownIcon, backgroundColor: `${color}1A`, color }}>
                  <Icon size={15} />
                </span>
                <div style={styles.breakdownBody}>
                  <div style={styles.breakdownHeader}>
                    <span style={styles.breakdownLabel}>{t[labelKey]}</span>
                    <span style={{ ...styles.breakdownValue, color }}>{value}</span>
                  </div>
                  <div style={styles.breakdownTrack}>
                    <div
                      style={{
                        ...styles.breakdownFill,
                        width: `${Math.min(100, Math.max(0, value))}%`,
                        backgroundColor: color,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <div style={styles.feedback}>
            <FeedbackBox text={breakdown.realLifeSummary} />
          </div>
        </Section>
      )}
    </ReadOnlyCard>
  );
}
